import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { MasterIndex } from "verdi-web-client/Models";
import { type ScheduleModeDevices, useCreateZoneEvent } from "@/api/hooks/zone-events/use-create-zone-event";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { useLocale } from "@/hooks/use-locale";
import { useSnackbar } from "@/hooks/use-snackbar/use-snackbar";
import { NAMESPACES } from "@/i18n/constants";
import type { DurationSource } from "@/services/analytics/events";
import { useOnDemandIrrigationEventTracker } from "@/services/analytics/irrigation/use-on-demand-irrigation-event-tracker";
import { confirmModal } from "@/services/simple-modal";
import { formatTimeAsHoursMinutesAndSeconds } from "verdi-common/utils/timeUtils";
import { DurationPicker } from "./duration-picker";
import { FormContainer } from "./form-container";
import { FormValidationMessage } from "./form-validation-message";
import { useIrrigationPresets } from "./irrigation-presets";
import { ResponseTimeHint } from "./response-time-hint";
import { StartTimePicker } from "./start-time-picker";

const TIME_FORMAT_OPTIONS = {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
} as const;

/**
 * Bounded so a device that keeps flipping into manual override can't trap the grower in a loop of
 * confirmations.
 */
/** Total create requests one click may send; each retry follows a confirmed acknowledgment. */
const MAX_CREATE_REQUESTS = 3;

interface EventInterval {
    startTime: Date;
    endTime: Date;
}

function checkOverlap(proposedStart: Date, proposedEnd: Date, events: EventInterval[]): boolean {
    // Uses <, not <=, so an event starting exactly when another ends is safe.
    return events.some((e) => proposedStart < e.endTime && e.startTime < proposedEnd);
}

interface CreateEventFormProps {
    onClose: () => void;
    zoneId: string;
    existingEvents?: EventInterval[];
    onPendingDurationChange?: (durationMs: number) => void;
    onPendingStartTimeChange?: (startTime: Date | null) => void;
    nowDate: Date;
}

export function CreateEventForm({
    onClose,
    zoneId,
    existingEvents = [],
    onPendingDurationChange,
    onPendingStartTimeChange,
    nowDate,
}: CreateEventFormProps) {
    const [durationMs, setDurationMs] = useState(0);
    const [durationError, setDurationError] = useState<string | null>(null);
    const [durationSource, setDurationSource] = useState<DurationSource>("preset");
    const [resolvedStartTime, setResolvedStartTime] = useState<Date | null>(null);
    const [startTimeError, setStartTimeError] = useState<string | null>(null);

    const { presets } = useIrrigationPresets(zoneId);
    const responseTimeMs = MasterIndex.zone.byID[zoneId]?.responseTimeStats?.theoreticalP95ActuationDelayMs;
    const { createZoneEvent, loading: createZoneEventLoading } = useCreateZoneEvent();
    const locale = useLocale();
    const { t } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.create-event-form",
    });
    const { t: tOnDemandValidationErrors } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.shared-validation-errors",
    });
    const { t: tManualModeWarning } = useTranslation(NAMESPACES.FEATURES.CARDS, {
        keyPrefix: "zone.on-demand-irrigation.manual-mode-warning",
    });
    const { t: tCommon } = useTranslation(NAMESPACES.COMMON);
    const { enqueueErrorSnackbar, enqueueSuccessSnackbar } = useSnackbar();
    const onDemandIrrigationEventTracker = useOnDemandIrrigationEventTracker(zoneId);

    const handleDurationChange = useCallback(
        (ms: number, error: string | null, source: DurationSource) => {
            setDurationMs(ms);
            setDurationError(error);
            setDurationSource(source);
            onPendingDurationChange?.(ms);
        },
        [onPendingDurationChange],
    );

    const handleStartTimeChange = useCallback(
        (startTime: Date | null, error: string | null) => {
            setStartTimeError(error);
            if (error !== null) return;
            setResolvedStartTime(startTime);
            onPendingStartTimeChange?.(startTime);
        },
        [onPendingStartTimeChange],
    );

    const displayedStartTime = resolvedStartTime ?? nowDate;
    const startTimeLabel = displayedStartTime.toLocaleTimeString(locale, TIME_FORMAT_OPTIONS);
    const endTimeLabel =
        durationMs > 0
            ? new Date(displayedStartTime.getTime() + durationMs).toLocaleTimeString(locale, TIME_FORMAT_OPTIONS)
            : null;

    const proposedEnd = durationMs > 0 ? new Date(displayedStartTime.getTime() + durationMs) : null;
    const overlapError =
        proposedEnd !== null && checkOverlap(displayedStartTime, proposedEnd, existingEvents)
            ? tOnDemandValidationErrors("event-overlap")
            : null;

    /**
     * Sends the create with the devices the grower has agreed may be switched out of manual override.
     * Returns the devices the server wants acknowledged instead when it refused the write.
     */
    const executeCreateEvent = useCallback(
        async (devicesToSetToScheduleMode: string[]): Promise<ScheduleModeDevices | null> => {
            // Recomputed per attempt: a confirmation the grower sat on moves the offset forward
            const startTimeOffsetMs = resolvedStartTime ? Math.max(0, resolvedStartTime.getTime() - Date.now()) : 0;

            const { created, devicesToAcknowledge } = await createZoneEvent({
                zoneId,
                startTimeOffsetMs,
                durationMs,
                devicesToSetToScheduleMode,
            });
            if (devicesToAcknowledge) {
                return devicesToAcknowledge;
            }

            if (created) {
                onDemandIrrigationEventTracker.trackCreate({
                    eventId: created.recordId ?? created._id,
                    durationMs,
                    isScheduledStart: resolvedStartTime !== null,
                    durationSource,
                });
                enqueueSuccessSnackbar(
                    t("snackbar.success", {
                        timeLabelWithUnit: formatTimeAsHoursMinutesAndSeconds(durationMs, {
                            includeSecondsWithMinutes: false,
                        }),
                    }),
                );
                onClose();
            } else {
                onDemandIrrigationEventTracker.trackCreateFail();
                enqueueErrorSnackbar(t("snackbar.error"));
            }
            return null;
        },
        [
            createZoneEvent,
            zoneId,
            durationMs,
            durationSource,
            resolvedStartTime,
            enqueueSuccessSnackbar,
            enqueueErrorSnackbar,
            onDemandIrrigationEventTracker,
            t,
            onClose,
        ],
    );

    const confirmScheduleMode = useCallback(
        async (devices: ScheduleModeDevices) => {
            const startTiming = resolvedStartTime
                ? formatTimeAsHoursMinutesAndSeconds(resolvedStartTime.getTime() - nowDate.getTime(), {
                      includeSecondsWithMinutes: false,
                  })
                : undefined;

            const choice = await confirmModal({
                title: tManualModeWarning("title"),
                body: (
                    <div className={"text-sm gap-2 flex flex-col"}>
                        <p>{tManualModeWarning("confirm-description")}</p>
                        <ul className={"list-disc list-inside font-medium"}>
                            {devices.map((device) => (
                                <li key={device.verdiId}>{device.name ?? device.verdiId}</li>
                            ))}
                        </ul>
                    </div>
                ),
                confirmLabel: startTiming
                    ? tManualModeWarning("confirm-label-delayed", { startTiming })
                    : tManualModeWarning("confirm-label-now"),
                cancelLabel: tCommon("actions.cancel"),
            });
            return choice === "confirm";
        },
        [tManualModeWarning, tCommon, resolvedStartTime, nowDate],
    );

    const onClickCreateEvent = useCallback(async () => {
        if (createZoneEventLoading) return;

        // Round one names no devices. A returned list is the server asking for acknowledgment before it
        // will create; anything falsy means the flow already ended (created, or failed and reported).
        let devicesToAcknowledge = await executeCreateEvent([]);
        let retriesLeft = MAX_CREATE_REQUESTS - 1;

        while (devicesToAcknowledge && retriesLeft > 0) {
            retriesLeft -= 1;
            if (!(await confirmScheduleMode(devicesToAcknowledge))) {
                return;
            }
            devicesToAcknowledge = await executeCreateEvent(devicesToAcknowledge.map((device) => device.verdiId));
        }

        if (devicesToAcknowledge) {
            // Still asking after the last allowed request — a device kept re-entering manual override
            onDemandIrrigationEventTracker.trackCreateFail();
            enqueueErrorSnackbar(tManualModeWarning("confirm-error"));
        }
    }, [
        createZoneEventLoading,
        executeCreateEvent,
        confirmScheduleMode,
        enqueueErrorSnackbar,
        onDemandIrrigationEventTracker,
        tManualModeWarning,
    ]);

    const createEventButtonLabel = resolvedStartTime
        ? t("buttons.create-delayed", {
              delayLabelWithUnit: formatTimeAsHoursMinutesAndSeconds(resolvedStartTime.getTime() - nowDate.getTime(), {
                  includeSecondsWithMinutes: false,
              }),
          })
        : t("buttons.create-now", {
              nowLabel: t("start-time.earliest-start-button"),
          });

    return (
        <FormContainer title={t("title")} onClose={onClose} variant={"create"}>
            <div className={"flex flex-col gap-4"}>
                <DurationPicker onDurationChange={handleDurationChange} presets={presets.duration} />
                <StartTimePicker onStartTimeChange={handleStartTimeChange} presets={presets.startTime} />
            </div>
            {overlapError && <FormValidationMessage errorMessage={overlapError} />}
            {endTimeLabel && (
                <div className={"text-muted-foreground flex flex-row items-center justify-between w-full gap-2"}>
                    <span>{t("event-time")}:</span>
                    <p className={"tabular-nums"}>
                        {startTimeLabel} – {endTimeLabel} (
                        {formatTimeAsHoursMinutesAndSeconds(durationMs, { includeSecondsWithMinutes: false })})
                    </p>
                </div>
            )}
            <div className={"flex flex-col items-center jusity-center gap-1"}>
                <Button
                    className={"w-full p-3"}
                    disabled={
                        durationMs <= 0 ||
                        durationError !== null ||
                        startTimeError !== null ||
                        overlapError !== null ||
                        createZoneEventLoading
                    }
                    onClick={onClickCreateEvent}
                >
                    {createZoneEventLoading ? <Spinner className={"size-4"} /> : createEventButtonLabel}
                </Button>
                <ResponseTimeHint responseTimeMs={responseTimeMs} className={"mt-0"} />
            </div>
        </FormContainer>
    );
}
